import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useRef, useState } from 'react';
import { Button, DataGrid, Icon, IconButton, MenuItem, Select, useSnackbar, Typography } from '@emburse/embark-core';
import { EditIcon, InfoIcon } from '@emburse/embark-icons';
import CorpDataInterchangeConfigDrawer from './CorpDataInterchangeConfigDrawer';
import { ExampleContent, Users } from './CorpDataInterchangeConfigDrawer/CorpDataInterchangeConfigDrawerContent';
import { CorpDataInterchangeStep3Context } from '../CorpDataInterchangeConfigStep3Provider';
import { $t, useTranslation } from '../../../hooks/useTranslation';
function CorpDataInterchangeConfigTable() {
    useTranslation();
    const drawerRef = useRef(null);
    const drawerContentRef = useRef(null);
    const { fileData, mappingValues, updateFileData } = useContext(CorpDataInterchangeStep3Context);
    const [drawerTitle, setDrawerTitle] = useState($t('Field Data Example'));
    const [selectedField, setSelectedField] = useState();
    const { enqueueSnackbar } = useSnackbar();
    const certifyFields = () => {
        const mappedValues = mappingValues.map((item) => {
            let isRequired;
            if (fileData.table === 'User') {
                // Check if user sync
                switch (item.Source) {
                    case 'Emp_EmployeeID':
                        // Controls user key
                        isRequired = fileData.key === item.Source || item.Required;
                        break;
                    case 'Dept_Name':
                        // Controls Dept key
                        isRequired = fileData.keyondeptname === true;
                        break;
                    case 'Dept_Code':
                        // Controls Dept key
                        isRequired = fileData.keyondeptname === false;
                        break;
                    default:
                        isRequired = item.Required;
                        break;
                }
            }
            else {
                // If not User Sync check if Key is not Name
                isRequired = fileData.key === item.Source || item.Required;
            }
            return { ...item, ...{ Required: isRequired ? 'Required' : null } };
        });
        return [...[{ Name: 'No Mapping', Source: ' ' }], ...mappedValues];
    };
    const hasDuplicates = (tableRows) => {
        const strArray = tableRows.map(({ CertifyField }) => CertifyField);
        return strArray.filter((item, index) => item.trim() !== '' && strArray.indexOf(item) !== index).length;
    };
    const getTableRowValue = (cell) => {
        const mapDataCopy = JSON.parse(JSON.stringify(fileData.mapData));
        const tableRow = mapDataCopy.find(({ id }) => id === cell.id);
        return tableRow.CertifyField;
    };
    const setTableRowValue = (e, cell) => {
        const mapDataCopy = JSON.parse(JSON.stringify(fileData.mapData));
        const tableRow = mapDataCopy.find(({ id }) => id === cell.id);
        if (tableRow.CertifyField === 'Emp_EmpTypeFK' && e.target.value !== 'Emp_EmpTypeFK') {
            delete tableRow.AdditionalAttribute;
        }
        tableRow.CertifyField = e.target.value;
        updateFileData({ mapData: mapDataCopy });
        if (hasDuplicates(mapDataCopy)) {
            enqueueSnackbar($t('Multiple fields cannot be mapped to one Certify field'), {
                variant: 'alert',
                AlertProps: { severity: 'error' }
            });
        }
    };
    const openDrawer = (cell, title) => {
        const mapDataCopy = JSON.parse(JSON.stringify(fileData.mapData));
        const tableRow = mapDataCopy.find(({ id }) => id === cell.id);
        setSelectedField(tableRow);
        setDrawerTitle(title);
        if (drawerRef.current) {
            drawerRef.current.setDrawerState(true);
        }
    };
    const drawerSaveHandler = (attributes) => {
        const mapDataCopy = JSON.parse(JSON.stringify(fileData.mapData));
        const tableRow = mapDataCopy.find(({ id }) => id === selectedField.id);
        tableRow.AdditionalAttribute = attributes;
        updateFileData({ mapData: mapDataCopy });
    };
    const saveClickHandler = () => {
        if (drawerContentRef.current) {
            drawerContentRef.current.onSave();
        }
        if (drawerRef.current) {
            drawerRef.current.setDrawerState(false);
        }
    };
    const drawerChildren = () => drawerTitle === $t('Field Data Example') ? (_jsx(ExampleContent, { selectedField: selectedField })) : (_jsx(Users, { selectedField: selectedField, saveHandler: drawerSaveHandler, ref: drawerContentRef }));
    return (_jsxs(_Fragment, { children: [_jsx(CorpDataInterchangeConfigDrawer, { title: drawerTitle, ref: drawerRef, onSave: saveClickHandler, children: drawerChildren() }), _jsx(DataGrid, { autoHeight: true, pageSizeOptions: [50, 100, 200], rows: fileData.mapData, columns: [
                    {
                        field: 'Column',
                        headerName: $t('Column'),
                        flex: 1,
                        renderCell: (cell) => _jsx(Typography, { children: cell.value })
                    },
                    {
                        field: 'Example',
                        headerName: $t('Example'),
                        flex: 6,
                        renderCell: (cell) => _jsx(Typography, { children: cell.value })
                    },
                    {
                        field: 'CertifyField',
                        headerName: $t('Certify Field'),
                        flex: 3,
                        renderCell: (cell) => (
                        // TDOD - Figure out auto populating fields with autocomplete
                        // <Autocomplete
                        //    id="CertifyField"
                        //    style={{ width: '100%', minWidth: 'auto' }}
                        //    options={certifyFields}
                        //    disableClearable
                        //    getOptionLabel={(option) => option.Name || 'No Mapping'}
                        //    defaultValue={cell.value}
                        //    onChange={(e, value) => setTableRowValue(value, cell)}
                        /// >
                        _jsx(Select, { id: "CertifyField", style: { width: '100%', minWidth: 'auto' }, defaultValue: cell.value, label: "", onChange: (e) => setTableRowValue(e, cell), children: certifyFields().map((item) => (_jsx(MenuItem, { value: item.Source, chip: item.Required, children: item.Name }, item.Source))) }))
                    },
                    {
                        field: 'AdditionalAttribute',
                        headerName: '',
                        flex: 4,
                        sortable: false,
                        renderCell: (cell) => 
                        // TODO - remove false
                        getTableRowValue(cell) === 'Emp_EmpTypeFK' && (_jsx(Button, { variant: "text", color: "primary", startIcon: _jsx(Icon, { iconUrl: EditIcon }), onClick: () => openDrawer(cell, $t('Additional Attributes')), style: { textTransform: 'none' }, children: "Additional attribute needed" }))
                    },
                    {
                        field: 'Info',
                        headerName: '',
                        sortable: false,
                        renderCell: (cell) => cell.row.ExampleData &&
                            cell.row.ExampleData.length && (_jsx("div", { style: { width: '100%', textAlign: 'right' }, children: _jsx(IconButton, { color: "secondary", size: "medium", title: "Info Button", onClick: () => openDrawer(cell, $t('Field Data Example')), children: _jsx(Icon, { iconUrl: InfoIcon }) }) }))
                    }
                ] })] }));
}
export default CorpDataInterchangeConfigTable;
